<script lang="ts">
  import { pushState } from "../push/store.svelte";
  import { requestAndSubscribe } from "../push/subscribe";

  // Dismiss client-side : si l'user dit "plus tard" on cache jusqu'au refresh
  // (volontairement éphémère, pas de localStorage — on veut le re-pousser à
  // chaque session tant qu'il n'a pas activé).
  let dismissed = $state(false);
  let error = $state<string | null>(null);

  async function activate() {
    error = null;
    try {
      await requestAndSubscribe();
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  // Visible si :
  //   - pas encore dismissed
  //   - pas déjà subscribed
  //   - permission pas explicitement refusée (sinon banner-info)
  const showActivate = $derived(
    !dismissed && !pushState.subscribed && pushState.permission !== "denied",
  );
</script>

{#if showActivate}
  <div class="banner activate">
    <div class="text">
      <strong>Activer les notifications push</strong>
      <span class="hint">
        Recevoir un push instantané dès qu'un destinataire ouvre un de tes mails trackés.
      </span>
    </div>
    <div class="actions">
      <button class="primary" onclick={activate} disabled={pushState.busy}>
        {pushState.busy ? "…" : "Activer"}
      </button>
      <button class="ghost" onclick={() => (dismissed = true)} disabled={pushState.busy}>
        Plus tard
      </button>
    </div>
    {#if error}
      <p class="error">{error}</p>
    {/if}
  </div>
{:else if pushState.permission === "denied"}
  <div class="banner blocked">
    <strong>Notifications bloquées.</strong>
    <span class="hint">
      Active-les dans Réglages iOS → Safari (ou ⓘ dans la barre d'URL en desktop).
    </span>
  </div>
{:else if pushState.subscribed}
  <!-- Optionnel : feedback subtle "✓ active". Sinon rien (silencieux = bon signe). -->
{/if}

<style>
  .banner {
    margin: 0 0 1.5rem;
    padding: 1rem;
    border-radius: 10px;
    border: 1px solid;
  }
  .banner.activate {
    background: color-mix(in oklch, var(--accent) 10%, transparent);
    border-color: color-mix(in oklch, var(--accent) 35%, transparent);
  }
  .banner.blocked {
    background: color-mix(in oklch, var(--bad) 8%, transparent);
    border-color: color-mix(in oklch, var(--bad) 30%, transparent);
    color: #991b1b;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 0.8rem;
  }
  .hint {
    font-size: 0.85rem;
    color: #57534e;
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  button {
    font: inherit;
    font-weight: 500;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    border: 1px solid transparent;
  }
  button.primary {
    background: var(--fg);
    color: var(--bg);
    border-color: var(--fg);
  }
  button.primary:disabled { opacity: 0.5; cursor: not-allowed; }
  button.ghost {
    background: transparent;
    color: #57534e;
    border-color: var(--border);
  }
  button.ghost:disabled { opacity: 0.5; cursor: not-allowed; }
  .error {
    margin: 0.8rem 0 0;
    font-size: 0.85rem;
    color: var(--bad);
  }

  @media (prefers-color-scheme: dark) {
    .hint { color: var(--fg-subtle); }
    button.primary {
      background: var(--bg);
      color: var(--fg);
      border-color: var(--bg);
    }
    button.ghost {
      color: var(--fg-subtle);
      border-color: var(--fg-muted);
    }
    .banner.blocked { color: #fca5a5; }
  }
</style>
